import React, { ComponentType } from "react"
import {
  PixelRatio,
  Pressable,
  PressableProps,
  PressableStateCallbackType,
  StyleProp,
  TextStyle,
  ViewStyle,
} from "react-native"

import { useAppTheme } from "app/hooks/useAppTheme"

import type { ThemedStyle, ThemedStyleArray } from "app/theme"
import { Text, TextProps } from "./Text"

type Presets = "default" | "filled" | "reversed" | "sidekick" | "subtle"

export interface ButtonAccessoryProps {
  style: StyleProp<any>
  pressableState: PressableStateCallbackType
  disabled?: boolean
}

export interface ButtonProps extends PressableProps {
  /**
   * Text which is looked up via i18n.
   */
  tx?: TextProps["tx"]
  /**
   * The text to display if not using `tx` or nested components.
   */
  text?: TextProps["text"]
  /**
   * Optional options to pass to i18n. Useful for interpolation
   * as well as explicitly setting locale or translation fallbacks.
   */
  txOptions?: TextProps["txOptions"]
  /**
   * An optional style override useful for padding & margin.
   */
  style?: StyleProp<ViewStyle>
  /**
   * An optional style override for the "pressed" state.
   */
  pressedStyle?: StyleProp<ViewStyle>
  /**
   * An optional style override for the button text.
   */
  textStyle?: StyleProp<TextStyle>
  /**
   * An optional style override for the button text when in the "pressed" state.
   */
  pressedTextStyle?: StyleProp<TextStyle>
  /**
   * An optional style override for the button text when in the "disabled" state.
   */
  disabledTextStyle?: StyleProp<TextStyle>
  /**
   * One of the different types of button presets.
   */
  preset?: Presets
  /**
   * An optional component to render on the right side of the text.
   * Example: `RightAccessory={(props) => <View {...props} />}`
   */
  RightAccessory?: ComponentType<ButtonAccessoryProps>
  /**
   * An optional component to render on the left side of the text.
   * Example: `LeftAccessory={(props) => <View {...props} />}`
   */
  LeftAccessory?: ComponentType<ButtonAccessoryProps>
  /**
   * Children components.
   */
  children?: React.ReactNode
  /**
   * disabled prop, accessed directly for declarative styling reasons.
   * https://reactnative.dev/docs/pressable#disabled
   */
  disabled?: boolean
  /**
   * An optional style override for the disabled state
   */
  disabledStyle?: StyleProp<ViewStyle>
}

/**
 * A component that allows users to take actions and make choices.
 * Wraps the Text component with a Pressable component.
 * @see [Documentation and Examples]{@link https://docs.infinite.red/ignite-cli/boilerplate/components/Button/}
 * @param {ButtonProps} props - The props for the `Button` component.
 * @returns {JSX.Element} The rendered `Button` component.
 * @example
 * <Button
 *   tx="common.ok"
 *   style={styles.button}
 *   textStyle={styles.buttonText}
 *   onPress={handleButtonPress}
 * />
 */
export function Button(props: ButtonProps) {
  const {
    tx,
    text,
    txOptions,
    style: $viewStyleOverride,
    pressedStyle: $pressedViewStyleOverride,
    textStyle: $textStyleOverride,
    pressedTextStyle: $pressedTextStyleOverride,
    disabledTextStyle: $disabledTextStyleOverride,
    children,
    RightAccessory,
    LeftAccessory,
    disabled,
    disabledStyle: $disabledViewStyleOverride,
    ...rest
  } = props

  const { themed } = useAppTheme()
  const fontScale = PixelRatio.getFontScale()
  // Explicit lineHeight mirrors the ButtonCluster/MessageBubble pattern: on Fabric
  // (New Architecture) Yoga can use stale cached text measurements unless the
  // lineHeight style value itself changes, so we derive it from the live fontScale.
  const scaledTextLineHeight = Math.ceil(16 * fontScale * 1.35)

  const preset: Presets = props.preset ?? "default"
  /**
   * @param {PressableStateCallbackType} root0 - The root object containing the pressed state.
   * @param {boolean} root0.pressed - The pressed state.
   * @returns {StyleProp<ViewStyle>} The view style based on the pressed state.
   */
  function $viewStyle({ pressed }: PressableStateCallbackType): StyleProp<ViewStyle> {
    return [
      themed($viewPresets[preset]),
      $viewStyleOverride,
      !!pressed && themed([$pressedViewPresets[preset], $pressedViewStyleOverride]),
      !!disabled && themed([$disabledViewPresets[preset], $disabledViewStyleOverride]),
    ]
  }
  /**
   * @param {PressableStateCallbackType} root0 - The root object containing the pressed state.
   * @param {boolean} root0.pressed - The pressed state.
   * @returns {StyleProp<TextStyle>} The text style based on the pressed state.
   */
  function $textStyle({ pressed }: PressableStateCallbackType): StyleProp<TextStyle> {
    return [
      themed($textPresets[preset]),
      { lineHeight: scaledTextLineHeight },
      $textStyleOverride,
      !!pressed && themed([$pressedTextPresets[preset], $pressedTextStyleOverride]),
      !!disabled && themed([$disabledTextPresets[preset], $disabledTextStyleOverride]),
    ]
  }

  return (
    <Pressable
      style={$viewStyle}
      accessibilityRole="button"
      accessibilityState={{ disabled: !!disabled }}
      {...rest}
      disabled={disabled}
    >
      {(state) => (
        <>
          {!!LeftAccessory && (
            <LeftAccessory style={$leftAccessoryStyle} pressableState={state} disabled={disabled} />
          )}

          <Text tx={tx} text={text} txOptions={txOptions} style={$textStyle(state)}>
            {children}
          </Text>

          {!!RightAccessory && (
            <RightAccessory
              style={$rightAccessoryStyle}
              pressableState={state}
              disabled={disabled}
            />
          )}
        </>
      )}
    </Pressable>
  )
}

const $baseViewStyle: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  minHeight: 56,
  borderRadius: 4,
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "row",
  paddingVertical: spacing.sm,
  paddingHorizontal: spacing.sm,
  overflow: "hidden",
})

const $baseTextStyle: ThemedStyle<TextStyle> = ({ typography }) => ({
  fontSize: 16,
  fontFamily: typography.primary.medium,
  textAlign: "center",
  flexShrink: 1,
  flexGrow: 0,
  zIndex: 2,
})

const $rightAccessoryStyle: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  marginStart: spacing.xs,
  zIndex: 1,
})
const $leftAccessoryStyle: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  marginEnd: spacing.xs,
  zIndex: 1,
})

const $viewPresets: Record<Presets, ThemedStyleArray<ViewStyle>> = {
  default: [
    $baseViewStyle,
    ({ colors }) => ({
      borderWidth: 1,
      borderColor: colors.buttonDefaultBorder,
      backgroundColor: colors.buttonDefaultBackground,
    }),
  ],
  filled: [$baseViewStyle, ({ colors }) => ({ backgroundColor: colors.buttonFilledBackground })],
  reversed: [
    $baseViewStyle,
    ({ colors }) => ({ backgroundColor: colors.buttonReversedBackground }),
  ],
  sidekick: [
    $baseViewStyle,
    ({ colors }) => ({
      backgroundColor: colors.buttonBackground,
      borderRadius: 28,
      borderWidth: 0,
    }),
  ],
  subtle: [
    ({ spacing }) => ({
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.md + 4,
      borderRadius: 12,
      minHeight: 0,
      justifyContent: "center",
      alignItems: "center",
      flexDirection: "row",
      overflow: "hidden",
    }),
    ({ colors }) => ({
      backgroundColor: colors.buttonBackground,
      borderWidth: 0,
    }),
  ],
}

const $textPresets: Record<Presets, ThemedStyleArray<TextStyle>> = {
  default: [$baseTextStyle],
  filled: [$baseTextStyle],
  reversed: [$baseTextStyle, ({ colors }) => ({ color: colors.buttonReversedText })],
  sidekick: [$baseTextStyle, ({ colors }) => ({ color: colors.menuButtonText, fontWeight: "600" })],
  subtle: [
    ({ typography }) => ({
      fontSize: 16,
      fontFamily: typography.primary.medium,
      textAlign: "center",
      flexShrink: 1,
      flexGrow: 0,
      zIndex: 2,
    }),
    ({ colors }) => ({ color: colors.menuButtonText, fontWeight: "500" }),
  ],
}

const $pressedViewPresets: Record<Presets, ThemedStyle<ViewStyle>> = {
  default: ({ colors }) => ({ backgroundColor: colors.buttonDefaultPressedBackground }),
  filled: ({ colors }) => ({ backgroundColor: colors.buttonFilledPressedBackground }),
  reversed: ({ colors }) => ({ backgroundColor: colors.buttonReversedPressedBackground }),
  sidekick: ({ colors }) => ({ backgroundColor: colors.buttonBackground }),
  subtle: ({ colors }) => ({ backgroundColor: colors.buttonBackground }),
}

const $pressedTextPresets: Record<Presets, ThemedStyle<TextStyle>> = {
  default: () => ({ opacity: 0.9 }),
  filled: () => ({ opacity: 0.9 }),
  reversed: () => ({ opacity: 0.9 }),
  sidekick: () => ({ opacity: 0.9 }),
  subtle: () => ({ opacity: 0.9 }),
}

const $disabledViewPresets: Record<Presets, ThemedStyle<ViewStyle>> = {
  default: () => ({}),
  filled: () => ({}),
  reversed: () => ({}),
  sidekick: () => ({}),
  subtle: () => ({}),
}

const $disabledTextPresets: Record<Presets, ThemedStyle<TextStyle>> = {
  default: () => ({}),
  filled: () => ({}),
  reversed: () => ({}),
  sidekick: ({ colors }) => ({ color: colors.menuButtonText }),
  subtle: () => ({}),
}
